import React from 'react';
import styled from '@emotion/styled';
import { FormattedMessage } from 'react-intl';
import Dimmer from 'views/shared/ui/dimmer';
import {
  Label,
  OkButton,
  CancelButton,
  Actions,
  Text,
} from 'ui_system/forms/FormStyles';
import { Theme } from 'ui_system/theme';
import { colors } from 'ui_system/compass';
import Warning from 'views/ui/icons/Warning';

import SimpleModal from 'ui_system/forms/SimpleModal';

const Title = styled(Label)`
  display: block;
  font-family: 'proxima-nova';
  text-align: center;
  font-size: 18px;
  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    font-size: 1.5rem;
    margin: 0 55px 25px;
  }
`;

const CenteredText = styled.div`
  display: block;
  text-align: center;
  margin-top: 10px;
  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    margin-top: 24px;
  }
`;

const ErrorWrapper = styled.div`
  display: flex;
  margin-top: 24px;
`;

const ErrorText = styled.p`
  display: inline-block;
  text-align: left;
  color: ${colors.error};
`;

const Divider = styled.div`
  border-bottom: 1px solid ${colors.border};
  width: 100px;
  margin: 0 auto;
  padding: 5px;
  position: relative;
  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    padding-top: 24px;
  }
`;

const SubText = styled(Text)`
  padding-bottom: 20px;
  text-align: center;
  display: block;
`;

const Confirm = styled(SimpleModal)`
  padding: 32px;
  max-width: 420px;
  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    max-height: ${(props) => (props.error ? '460px' : '380px')};
    max-width: 360px;
  }
`;

const ButtonActions = styled(Actions)`
  margin-top: 20%;
  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    grid-template-columns: 100%;
  }
`;

const WarningIcon = styled(Warning)`
  height: 24px;
  width: 24px;
  fill: ${colors.error};
  margin-right: 8px;
`;

const KeepBookingButton = styled(CancelButton)`
  height: 50px;
`;

const CancelBookingButton = styled(OkButton)`
  height: 50px;
`;

const BookingCancelModal = (props) => {
  const {
    open,
    close,
    label,
    text,
    refundAmount,
    cancellationPolicy,
    onCancelBooking,
    onKeepBooking,
    isFree,
    cancelLoading,
    cancelError,
  } = props;
  return (
    <Confirm
      className="cancel-booking-modal"
      open={open}
      close={close}
      preventClickOff
      zIndex={100001}
      error={cancelError}
    >
      {cancelLoading && (
        <Dimmer loader={cancelLoading} active={cancelLoading} fixed />
      )}
      <Title className="cancel-modal-title" allowWrap>
        {label}
      </Title>
      {isFree ? (
        <SubText className="cancel-modal-subtext" small>
          {text}
        </SubText>
      ) : (
        <>
          <CenteredText
            data-testid="cancel-modal-refund"
            className="cancel-modal-refund"
          >
            <FormattedMessage
              id="REFUND_AMOUNT_FORMATTED"
              defaultMessage="Refund amount: <strong>{refundAmount}</strong>"
              values={{
                refundAmount,
                strong: (chunks) => <strong>{chunks}</strong>,
              }}
            />
          </CenteredText>
          <Divider />
          <CenteredText className="cancel-modal-cancellation-policy">
            <strong>
              <FormattedMessage
                id="CANCELLATION_POLICY"
                defaultMessage="Cancellation policy"
              />
              :
            </strong>{' '}
            {cancellationPolicy}
          </CenteredText>
        </>
      )}
      {cancelError && (
        <ErrorWrapper>
          <WarningIcon className="cancel-modal-error-icon" />
          <span>
            <ErrorText className="cancel-modal-error-text">
              <FormattedMessage
                id="CANCELLATION_ERROR"
                defaultMessage="Something went wrong and we could not cancel your booking. Please try again."
              />
            </ErrorText>
          </span>
        </ErrorWrapper>
      )}
      <ButtonActions gridTemplateColumns="1fr 1fr">
        <KeepBookingButton
          className="cancel-modal-keep-booking-button"
          justify="end"
          onClick={onKeepBooking}
          data-testid="keep-booking-button"
        >
          <FormattedMessage id="DONT_CANCEL" defaultMessage="Don''t cancel" />
        </KeepBookingButton>
        <CancelBookingButton
          data-testid="cancel-booking-confirm-btn"
          justify="start"
          onClick={onCancelBooking}
          className="brand-bg cancel-modal-cancel-button"
        >
          <FormattedMessage
            id="CANCEL_BOOKING"
            defaultMessage="Cancel booking"
          />
        </CancelBookingButton>
      </ButtonActions>
    </Confirm>
  );
};

export default BookingCancelModal;
